import React, { useCallback, useContext, useMemo, useState } from "react";
import { Chip, Menu, MenuItem } from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { useStatusArray } from "@/hooks";

interface StatusOption {
  value: string;
  label: string;
  color: string;
}

export type StatusChangeHandler<TRow = Record<string, any>> = (
  row: TRow,
  newStatus: string,
) => void;

interface UseStatusColumnProps<TRow = Record<string, any>> {
  onStatusChange: StatusChangeHandler<TRow>;
  updating?: boolean;
  fieldName?: string;
  customStatusOptions?: StatusOption[] | null;
}

interface CreateStatusColumnProps<TRow = Record<string, any>> {
  onStatusClick?: (_event: React.MouseEvent<HTMLElement>, _row: TRow) => void;
  gettext: (_value: string) => string;
  statusOptions: StatusOption[];
  fieldName?: string;
}

export function useStatusColumn<TRow = Record<string, any>>({
  onStatusChange,
  updating = false,
  fieldName = "status",
  customStatusOptions = null,
}: UseStatusColumnProps<TRow>): {
  statusColumn: Record<string, any>;
  StatusMenu: () => React.ReactElement;
  handleStatusMenuOpen: (
    _event: React.MouseEvent<HTMLElement>,
    _row: TRow,
  ) => void;
  handleStatusMenuClose: () => void;
} {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [selectedRow, setSelectedRow] = useState<TRow | null>(null);

  const defaultStatusOptions = useStatusArray() as StatusOption[];
  const statusOptions =
    customStatusOptions && customStatusOptions.length > 0
      ? customStatusOptions
      : defaultStatusOptions;

  const handleStatusMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: TRow) => {
      setStatusMenuAnchor(event.currentTarget);
      setSelectedRow(row);
    },
    [],
  );

  const handleStatusMenuClose = useCallback(() => {
    setStatusMenuAnchor(null);
    setSelectedRow(null);
  }, []);

  const handleStatusSelect = useCallback(
    (newStatus: string) => {
      if (selectedRow && onStatusChange) {
        onStatusChange(selectedRow as TRow, newStatus);
        setTimeout(() => {
          setStatusMenuAnchor(null);
          setSelectedRow(null);
        }, 300);
      } else {
        setStatusMenuAnchor(null);
        setSelectedRow(null);
      }
    },
    [selectedRow, onStatusChange],
  );

  const statusColumn = useMemo(
    () => ({
      field: fieldName,
      headerName: gettext("Status"),
      headerAlign: "center",
      type: "string",
      minWidth: 150,
      maxWidth: 200,
      align: "center",
      renderCell: (params: { value: string; row: any }) => {
        const statusObject = statusOptions.find(
          (row) => row.value === params.value,
        );

        return (
          <Chip
            label={params.value}
            color={(statusObject?.color as any) || "warning"}
            onClick={(event) => handleStatusMenuOpen(event, params.row as TRow)}
            sx={{ cursor: "pointer" }}
          />
        );
      },
    }),
    [fieldName, gettext, statusOptions, handleStatusMenuOpen],
  );

  const StatusMenu = useCallback(
    () => (
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disableAutoFocusItem={updating}
        disableRestoreFocus
        disableEnforceFocus
      >
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusSelect(option.value)}
            selected={(selectedRow as any)?.[fieldName] === option.value}
            disabled={updating}
          >
            <Chip
              label={option.label}
              color={option.color as any}
              size="small"
              sx={{ minWidth: 100 }}
            />
          </MenuItem>
        ))}
      </Menu>
    ),
    [
      statusMenuAnchor,
      handleStatusMenuClose,
      updating,
      statusOptions,
      handleStatusSelect,
      selectedRow,
      fieldName,
    ],
  );

  return {
    statusColumn,
    StatusMenu,
    handleStatusMenuOpen,
    handleStatusMenuClose,
  };
}

export function createStatusColumn({
  onStatusClick,
  gettext,
  statusOptions,
  fieldName = "status",
}: CreateStatusColumnProps): Record<string, any> {
  return {
    field: fieldName,
    headerName: gettext("Status"),
    headerAlign: "center",
    type: "string",
    minWidth: 150,
    maxWidth: 200,
    align: "center",
    renderCell: (params: { value: string; row: any }) => {
      const statusObject = statusOptions.find(
        (row) => row.value === params.value,
      );

      return (
        <Chip
          label={params.value}
          color={(statusObject?.color as any) || "warning"}
          onClick={(event) => onStatusClick?.(event, params.row as any)}
          sx={{ cursor: "pointer" }}
          size="small"
        />
      );
    },
  };
}
